import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { tenantApi } from '../../services/api';
import socketService from '../../services/socket';
import TenantHeader from '../../components/msp/TenantHeader';
import './Msp.css';

const normalizeList = (payload, key) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.[key])) return payload[key];
  return [];
};

function TenantExternalScans() {
  const { tenantId } = useParams();
  const [scans, setScans] = useState([]);
  const [status, setStatus] = useState('loading');
  const [progress, setProgress] = useState(null);
  const [triggerStatus, setTriggerStatus] = useState('idle');
  const [scanOptions, setScanOptions] = useState({
    scope: 'external',
    intensity: 'standard',
  });
  const [scanConfig, setScanConfig] = useState({
    scope: 'external',
    intensity: 'standard',
    schedule: 'monthly',
  });
  const [configStatus, setConfigStatus] = useState('idle');
  const [filters, setFilters] = useState({ status: '', startDate: '', endDate: '' });
  const [baselineScan, setBaselineScan] = useState(null);
  const [baselineStatus, setBaselineStatus] = useState('idle');

  const loadScans = useCallback(() => {
    setStatus('loading');
    return tenantApi
      .listScans(tenantId, { type: 'external' })
      .then((response) => {
        setScans(normalizeList(response.data, 'scans'));
        setStatus('ready');
      })
      .catch(() => {
        setStatus('error');
      });
  }, [tenantId]);

  const loadBaseline = useCallback(() => {
    tenantApi
      .getBaselineScan(tenantId)
      .then((response) => {
        setBaselineScan(response.data?.baseline_scan ?? null);
      })
      .catch(() => {
        setBaselineScan(null);
      });
  }, [tenantId]);

  useEffect(() => {
    loadScans();
    loadBaseline();
    // Load current scan config
    tenantApi
      .getScanConfig(tenantId)
      .then((response) => {
        const config = response.data?.scan_config || {};
        setScanConfig({
          scope: config.scope || 'external',
          intensity: config.intensity || 'standard',
          schedule: config.schedule || 'monthly',
        });
        setScanOptions({
          scope: config.scope || 'external',
          intensity: config.intensity || 'standard',
        });
      })
      .catch(() => {
        // Use defaults if config load fails
      });
  }, [loadScans, loadBaseline, tenantId]);

  useEffect(() => {
    socketService.connect();
    socketService.on('externalScanStarted', (data) => {
      const eventTenantId = data?.tenant_id;
      if (eventTenantId && parseInt(eventTenantId) !== parseInt(tenantId)) return;
      setProgress({ status: 'started', percent: 0, message: 'External scan started' });
    });
    socketService.on('externalScanProgress', (data) => {
      const eventTenantId = data?.tenant_id;
      if (eventTenantId && parseInt(eventTenantId) !== parseInt(tenantId)) return;
      setProgress({
        status: 'running',
        percent: data?.progress || 0,
        message: data?.message || 'Scanning external assets…',
      });
    });
    socketService.on('externalScanCompleted', (data) => {
      const eventTenantId = data?.tenant_id;
      if (eventTenantId && parseInt(eventTenantId) !== parseInt(tenantId)) return;
      setProgress({ status: 'completed', percent: 100, message: 'External scan completed' });
      loadScans();
    });
    socketService.on('externalScanFailed', (data) => {
      const eventTenantId = data?.tenant_id;
      if (eventTenantId && parseInt(eventTenantId) !== parseInt(tenantId)) return;
      setProgress({ status: 'failed', percent: 0, message: data?.error || 'External scan failed' });
      loadScans();
    });

    return () => {
      socketService.off('externalScanStarted');
      socketService.off('externalScanProgress');
      socketService.off('externalScanCompleted');
      socketService.off('externalScanFailed');
    };
  }, [tenantId, loadScans]);

  const handleTriggerScan = async () => {
    setTriggerStatus('submitting');
    try {
      await tenantApi.triggerExternalScan(tenantId, {
        scope: scanOptions.scope,
        intensity: scanOptions.intensity,
      });
      setTriggerStatus('success');
      // Reset to idle after a short delay
      setTimeout(() => setTriggerStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to trigger external scan', error);
      setTriggerStatus('error');
    }
  };

  const handleConfigSave = async (event) => {
    event.preventDefault();
    setConfigStatus('submitting');
    try {
      await tenantApi.updateScanConfig(tenantId, scanConfig);
      setConfigStatus('success');
      // Update scanOptions to match saved config
      setScanOptions({
        scope: scanConfig.scope,
        intensity: scanConfig.intensity,
      });
      setTimeout(() => setConfigStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to update scan config', error);
      setConfigStatus('error');
    }
  };

  const handleRetryScan = async (scanId) => {
    try {
      await tenantApi.retryScan(tenantId, scanId);
      loadScans();
    } catch (error) {
      console.error('Failed to retry scan', error);
    }
  };

  const handleCancelScan = async (scanId) => {
    try {
      await tenantApi.cancelScan(tenantId, scanId);
      loadScans();
    } catch (error) {
      console.error('Failed to cancel scan', error);
    }
  };

  const filteredScans = scans.filter((scan) => {
    const statusMatch = filters.status ? scan.status === filters.status : true;
    const startedAt = scan.started_at || scan.created_at;
    const startDateMatch = filters.startDate ? new Date(startedAt) >= new Date(filters.startDate) : true;
    const endDateMatch = filters.endDate ? new Date(startedAt) <= new Date(filters.endDate) : true;
    return statusMatch && startDateMatch && endDateMatch;
  });

  const handleDownloadReport = async (scanId) => {
    try {
      const response = await tenantApi.exportExternalScanReport(tenantId, scanId, 'pdf');
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Tenant_${tenantId}_External_Scan_${scanId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download external scan report', error);
    }
  };

  const handleSetBaseline = async (scanId) => {
    setBaselineStatus('submitting');
    try {
      await tenantApi.setBaselineScan(tenantId, scanId);
      loadScans();
      loadBaseline();
      setBaselineStatus('idle');
    } catch (error) {
      console.error('Failed to set baseline scan', error);
      setBaselineStatus('error');
    }
  };

  const handleClearBaseline = async () => {
    setBaselineStatus('submitting');
    try {
      await tenantApi.clearBaseline(tenantId);
      loadScans();
      setBaselineScan(null);
      setBaselineStatus('idle');
    } catch (error) {
      console.error('Failed to clear baseline', error);
      setBaselineStatus('error');
    }
  };

  return (
    <div className="msp-page">
      <TenantHeader
        tenantId={tenantId}
        tenantName="External Scans"
        subtitle="Trigger and track tenant exposure scans"
      >
        <div className="msp-inline-form" style={{ gap: '8px', marginRight: '12px' }}>
          <select
            className="msp-input"
            value={scanOptions.scope}
            onChange={(e) => setScanOptions({ ...scanOptions, scope: e.target.value })}
            style={{ minWidth: '120px' }}
          >
            <option value="external">External</option>
            <option value="full">Full Surface</option>
          </select>
          <select
            className="msp-input"
            value={scanOptions.intensity}
            onChange={(e) => setScanOptions({ ...scanOptions, intensity: e.target.value })}
            style={{ minWidth: '120px' }}
          >
            <option value="light">Light</option>
            <option value="standard">Standard</option>
            <option value="deep">Deep</option>
          </select>
        </div>
        <button className="btn btn-primary" onClick={handleTriggerScan} disabled={triggerStatus === 'submitting'}>
          {triggerStatus === 'submitting' ? 'Starting…' : 'Trigger Scan'}
        </button>
      </TenantHeader>

      {progress && (

        <div className="msp-progress">
          <div className="msp-progress-bar" style={{ width: `${progress.percent || 0}%` }}></div>
          <span className="msp-progress-text">{progress.message}</span>
        </div>
      )}

      {triggerStatus === 'error' && <div className="msp-empty">Unable to start scan.</div>}
      {triggerStatus === 'success' && (
        <div className="msp-empty" style={{ color: 'var(--neon-cyan)' }}>
          Scan triggered successfully with {scanOptions.scope} scope and {scanOptions.intensity} intensity.
        </div>
      )}

      <div className="msp-section">
        <div className="msp-section-title">Default Scan Configuration</div>
        <form className="msp-form" onSubmit={handleConfigSave}>
          <label className="msp-form-field">
            Scope
            <select
              className="msp-input"
              value={scanConfig.scope}
              onChange={(event) => setScanConfig({ ...scanConfig, scope: event.target.value })}
            >
              <option value="external">External</option>
              <option value="full">Full Surface</option>
            </select>
          </label>
          <label className="msp-form-field">
            Intensity
            <select
              className="msp-input"
              value={scanConfig.intensity}
              onChange={(event) => setScanConfig({ ...scanConfig, intensity: event.target.value })}
            >
              <option value="light">Light</option>
              <option value="standard">Standard</option>
              <option value="deep">Deep</option>
            </select>
          </label>
          <label className="msp-form-field">
            Schedule
            <select
              className="msp-input"
              value={scanConfig.schedule}
              onChange={(event) => setScanConfig({ ...scanConfig, schedule: event.target.value })}
            >
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </select>
          </label>
          <button type="submit" className="btn btn-primary" disabled={configStatus === 'submitting'}>
            {configStatus === 'submitting' ? 'Saving…' : 'Save Default Config'}
          </button>
        </form>
        {configStatus === 'error' && <div className="msp-empty">Unable to save scan config.</div>}
        {configStatus === 'success' && (
          <div className="msp-empty" style={{ color: 'var(--neon-cyan)' }}>
            Default configuration saved. New scans will use these settings.
          </div>
        )}
      </div>

      <div className="msp-section">
        <div className="msp-section-title">Baseline Scan</div>
        <p className="msp-description">
          Set a completed scan as the baseline. Future scans will compare against this baseline to detect changes.
        </p>
        {baselineScan ? (
          <div className="msp-inline-form" style={{ alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span>
              Current baseline: Scan #{baselineScan.id} from {baselineScan.started_at || baselineScan.completed_at || '—'}
            </span>
            <Link
              to={`/msp/tenants/${tenantId}/external-scans/${baselineScan.id}`}
              className="btn btn-secondary"
              style={{ padding: '6px 12px', fontSize: '12px' }}
            >
              View baseline
            </Link>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClearBaseline}
              disabled={baselineStatus === 'submitting'}
            >
              {baselineStatus === 'submitting' ? 'Clearing…' : 'Clear baseline'}
            </button>
          </div>
        ) : (
          <div className="msp-empty" style={{ marginBottom: '8px' }}>
            No baseline set. Complete a scan and use &quot;Set as baseline&quot; in the scan history below.
          </div>
        )}
        {baselineStatus === 'error' && (
          <div className="msp-empty" style={{ color: 'var(--danger)' }}>Baseline action failed.</div>
        )}
      </div>

      <div className="msp-toolbar">
        <select
          className="msp-input"
          value={filters.status}
          onChange={(event) => setFilters({ ...filters, status: event.target.value })}
        >
          <option value="">All statuses</option>
          <option value="queued">Queued</option>
          <option value="running">Running</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
        </select>
        <input
          type="date"
          className="msp-input"
          value={filters.startDate}
          onChange={(event) => setFilters({ ...filters, startDate: event.target.value })}
        />
        <input
          type="date"
          className="msp-input"
          value={filters.endDate}
          onChange={(event) => setFilters({ ...filters, endDate: event.target.value })}
        />
      </div>

      {status === 'loading' && <div className="msp-empty">Loading scan history…</div>}
      {status === 'error' && <div className="msp-empty">Unable to load scan history.</div>}
      {status === 'ready' && filteredScans.length === 0 && (
        <div className="msp-empty">No external scans recorded yet.</div>
      )}
      {status === 'ready' && filteredScans.length > 0 && (
        <table className="msp-table">
          <thead>
            <tr>
              <th>Scan ID</th>
              <th>Status</th>
              <th>Baseline</th>
              <th>Scope</th>
              <th>Intensity</th>
              <th>Findings</th>
              <th>Ports</th>
              <th>Started</th>
              <th>Completed</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredScans.map((scan) => (
              <tr key={scan.id || scan.scan_id}>
                <td>{scan.id || scan.scan_id}</td>
                <td>{scan.status}</td>
                <td>
                  {scan.is_baseline ? (
                    <span className="msp-badge" style={{ background: 'var(--neon-cyan)', color: '#000' }}>Baseline</span>
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  <span className="msp-badge msp-badge-muted">
                    {(scan.scan_metadata?.scope || scan.scope || 'external').toUpperCase()}
                  </span>
                </td>
                <td>
                  <span className="msp-badge msp-badge-muted">
                    {(scan.scan_intensity || scan.intensity || 'standard').toUpperCase()}
                  </span>
                </td>
                <td>
                  {scan.high_findings || 0} high / {scan.critical_findings || 0} critical
                </td>
                <td>
                  {typeof scan.open_ports === 'number'
                    ? `${scan.open_ports} open / ${scan.ports_scanned || 0} scanned`
                    : '—'}
                </td>
                <td>{scan.started_at || scan.created_at || '—'}</td>
                <td>{scan.completed_at || '—'}</td>
                <td>
                  {scan.status === 'failed' && (
                    <button type="button" className="btn btn-secondary" onClick={() => handleRetryScan(scan.id || scan.scan_id)}>
                      Retry
                    </button>
                  )}
                  {scan.status === 'running' && (
                    <button type="button" className="btn btn-secondary" onClick={() => handleCancelScan(scan.id || scan.scan_id)}>
                      Cancel
                    </button>
                  )}
                  {scan.status === 'completed' && (
                    <>
                      <Link
                        to={`/msp/tenants/${tenantId}/external-scans/${scan.id || scan.scan_id}`}
                        className="btn btn-primary"
                        style={{ padding: '6px 14px', fontSize: '12px', marginRight: '6px' }}
                      >
                        View Details
                      </Link>
                      {!scan.is_baseline && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => handleSetBaseline(scan.id || scan.scan_id)}
                          disabled={baselineStatus === 'submitting'}
                          style={{ marginRight: '6px' }}
                        >
                          Set as baseline
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => handleDownloadReport(scan.id || scan.scan_id)}
                      >
                        Download Report
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default TenantExternalScans;
